import {
  Avatar,
  Box,
  Dialog,
  Divider,
  IconButton,
  Link,
  List,
  ListItem,
  Typography
} from '@mui/material'
import type React from 'react'
import { MButton } from 'components'
import { useMobileBreakpoints } from 'utils/hooks/useMobileBreakpoints'
import PrivacyTipIcon from '@mui/icons-material/PrivacyTip'
import CloseIcon from '@mui/icons-material/Close'

interface PrivacyDialogProps {
  open: boolean
  handleClick: () => void
}

export const PrivacyDialog: React.FC<PrivacyDialogProps> = ({
  open,
  handleClick
}) => {
  const isMobile = useMobileBreakpoints()
  const handleLogin = (): void => {
    handleClick()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClick}
      PaperProps={{
        sx: {
          width: isMobile ? '90%' : '80%',
          maxWidth: '100%',
          m: 2
        }
      }}
    >
      <IconButton
        onClick={handleClick}
        sx={{ position: 'absolute', right: 8, top: 8 }}
      >
        <CloseIcon />
      </IconButton>
      <Box px={isMobile ? 5 : 5} py={7} maxWidth='100%'>
        <Box display='flex' flexDirection='column' alignItems='center'>
          <Avatar sx={{ background: '#0092bd', p: 3 }}>
            <PrivacyTipIcon sx={{ fontSize: 50, color: '#fff' }} />
          </Avatar>
        </Box>

        <Box mt={2}>
          <Typography
            textAlign='center'
            fontSize={isMobile ? 18 : 24}
            fontWeight={600}
            mb={5}
          >
            MyHUB Nationwide Energy Partners Privacy Policy & Utility
            Notification Enrollment Statement
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            MyHUB provides the customer portal for Nationwide Energy Partners
            (&quot;NEP&quot;) customers. MyHUB is committed to protecting the
            privacy and security of customer information. This policy explains
            how we collect, use, store, and safeguard personal data, including
            mobile numbers used for SMS, email, and automated voice
            notifications.
          </Typography>

          <Divider sx={{ py: 1 }} />

          <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>
            1. How Personal Data Is Collected and Used
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            We collect personal information directly from customers when they
            establish utility service, create an online account, update contact
            information, make a payment, or interact with the MyHUB customer
            portal. Information collected may include:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>Name</ListItem>
            <ListItem>Service address</ListItem>
            <ListItem>Email address</ListItem>
            <ListItem>Mobile phone number</ListItem>
            <ListItem>Account number</ListItem>
            <ListItem>Communication preferences</ListItem>
            <ListItem>Billing, usage, and payment information</ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mt={1}>
            Information provided by NEP may also include:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>Utility account identifiers</ListItem>
            <ListItem>Account status</ListItem>
            <ListItem>
              Usage or billing reference information (display-only)
            </ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mt={1}>
            We use this information to:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>Provide access to the MyHUB portal</ListItem>
            <ListItem>Display NEP account and service information</ListItem>
            <ListItem>
              Send billing reminders, payment confirmations, outage alerts, and
              important service notifications
            </ListItem>
            <ListItem>Maintain and secure customer accounts</ListItem>
            <ListItem>Verify customer identity</ListItem>
            <ListItem>Support customer service operations</ListItem>
            <ListItem>Improve our systems and communications</ListItem>
            <ListItem>Comply with legal and regulatory requirements</ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mt={2}>
            MyHUB does not collect or store Social Security numbers, full
            payment card numbers, bank account numbers, or government-issued
            identification numbers.
          </Typography>

          <Typography fontSize={16} fontWeight={400} mt={1} mb={2}>
            MyHUB does not sell customer information for marketing or commercial
            purposes.
          </Typography>

          <Divider sx={{ py: 1 }} />

          <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>
            2. Utility Notification Enrollment (SMS, Email, IVR)
          </Typography>

          <Typography fontSize={16} fontWeight={600} mb={1}>
            Emergency & Critical Notifications
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            Certain emergency or critical service notifications may be delivered
            automatically when required for public safety or service continuity.
            These may include:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>Unplanned outages</ListItem>
            <ListItem>Emergency service disruptions</ListItem>
            <ListItem>Safety-related alerts</ListItem>
            <ListItem>Public health notices</ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            These notifications are essential to utility operations and do not
            require prior opt-in.
          </Typography>

          <Typography fontSize={16} fontWeight={600} mb={1}>
            Account & Service Notifications
          </Typography>

          <Typography fontSize={16} fontWeight={400}>
            Customers may choose to receive non-emergency notifications related
            to their account. These may include:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>Billing reminders</ListItem>
            <ListItem>Payment confirmations</ListItem>
            <ListItem>Planned outage notifications</ListItem>
            <ListItem>Service updates</ListItem>
            <ListItem>Account-related notices</ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mt={1}>
            Customers may opt in by:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>Enabling notifications within the MyHUB portal</ListItem>
            <ListItem>Providing a mobile number during account setup</ListItem>
            <ListItem>Updating communication preferences</ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mb={1}>
            Message and data rates may apply.
          </Typography>
          <Typography fontSize={16} fontWeight={400} mb={2}>
            Message frequency varies based on account activity.
          </Typography>

          <Typography fontSize={16} fontWeight={600} mb={1}>
            Opt-Out Options
          </Typography>

          <Typography fontSize={16} fontWeight={400}>
            Customers may opt out of SMS notifications at any time by:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>
              Replying <b>STOP</b> to any MyHUB text message
            </ListItem>
            <ListItem>
              Updating notification preferences in the MyHUB portal
            </ListItem>
            <ListItem>Contacting NEP Customer Service</ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            Reply <b>HELP</b> for additional support.
            <br />
            Re-enrollment in SMS may require replying <b>START</b> or enabling
            SMS again in the MyHUB portal.
          </Typography>

          <Typography fontSize={16} fontWeight={600} mb={1}>
            Customer Consent
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            By providing a mobile phone number or enabling SMS notifications in
            the MyHUB portal, customers consent to receive automated text
            messages related to their account as described in this policy.
            <br />
            MyHUB does not share, sell, rent, or use customer mobile information
            for marketing or promotional purposes. Mobile numbers are used only
            to provide utility-related information and account services.
          </Typography>

          <Divider sx={{ py: 1 }} />

          <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>
            3. Disclosure of Personal Data to Third Parties
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={1}>
            MyHUB may share limited customer information with trusted
            third-party service providers only when necessary to deliver utility
            services or support operations, including:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>Nationwide Energy Partners</ListItem>
            <ListItem>Billing and payment processors</ListItem>
            <ListItem>SMS, email, and IVR messaging platforms</ListItem>
            <ListItem>
              Cloud hosting, security, and IT service providers
            </ListItem>
            <ListItem>Regulatory agencies when required by law</ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mt={1}>
            All third-party vendors are contractually required to:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>Protect the data they receive</ListItem>
            <ListItem>Use it solely to support MyHUB and NEP services</ListItem>
            <ListItem>
              Maintain compliance with applicable privacy and security standards
            </ListItem>
            <ListItem>
              Prohibit further disclosure or unauthorized access
            </ListItem>
          </List>

          <Typography fontSize={16} fontWeight={600} mb={2}>
            MyHUB does not authorize third parties to use customer data for
            advertising or unrelated purposes.
          </Typography>

          <Divider sx={{ py: 1 }} />

          <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>
            4. How Data Is Stored and Protected
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            MyHUB uses administrative, technical, and physical safeguards to
            protect customer information, including:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>Encrypted transmission of data</ListItem>
            <ListItem>
              Secure, encrypted storage of personal information
            </ListItem>
            <ListItem>Access controls and authentication requirements</ListItem>
            <ListItem>Monitoring, auditing, and vulnerability testing</ListItem>
            <ListItem>
              Regular system reviews and vendor security assessments
            </ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            Customer data is retained only as long as necessary to deliver
            services, maintain regulatory records, or meet legal obligations.
          </Typography>

          <Divider sx={{ py: 1 }} />

          <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>
            5. How Mobile Phone Numbers Are Handled
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            Mobile phone numbers are collected exclusively to deliver utility
            service-related notifications. Mobile numbers are:
          </Typography>

          <List
            sx={{
              listStyleType: 'disc',
              pl: 3,
              '& .MuiListItem-root': { display: 'list-item', paddingLeft: 0.5 }
            }}
          >
            <ListItem>
              Used only for essential account and service communications
            </ListItem>
            <ListItem>
              Shared only with contracted messaging and communication processors
            </ListItem>
            <ListItem>
              Protected using industry-standard encryption and security measures
            </ListItem>
            <ListItem>
              Never sold, rented, shared, or used for marketing purposes
            </ListItem>
            <ListItem>
              Able to be updated or removed at any time through the MyHUB portal
              or NEP Customer Service
            </ListItem>
          </List>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            Customers may opt out of SMS notifications at any time by replying{' '}
            <b>STOP</b>.
          </Typography>

          <Divider sx={{ py: 1 }} />

          <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>
            6. Linked Sites
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            The MyHUB portal may include links to third-party websites that
            offer related services or useful information. These websites have
            their own privacy policies. MyHUB is not responsible for the privacy
            practices or content of external sites.
          </Typography>

          <Divider sx={{ py: 1 }} />

          <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>
            7. Changes to This Privacy Policy
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            MyHUB may update this Privacy Policy from time to time. Updates will
            be reflected by revising the &quot;Last Updated&quot; date and
            posting the updated policy within the MyHUB portal or on the NEP
            website.
            <br />
            Continued use of the MyHUB portal after updates are posted
            constitutes acceptance of the revised Privacy Policy.
          </Typography>

          <Divider sx={{ py: 1 }} />

          <Typography fontSize={18} fontWeight={600} mt={2} mb={1}>
            8. Contact Information
          </Typography>

          <Typography fontSize={16} fontWeight={400} mb={2}>
            For questions about this Privacy Policy or how your information is
            handled, please contact:
            <br />
            <br />
            <b>Nationwide Energy Partners – Customer Support</b>
            <br />
            Phone: 877-818-2637
            <br />
            Email:{' '}
            <a href='mailto:support@nationwideenergypartners.com'>
              support@nationwideenergypartners.com
            </a>
            <br />
            Website:{' '}
            <Link
              target='_blank'
              rel='noopener noreferrer'
              href='https://www.nationwideenergypartners.com/'
            >
              www.nationwideenergypartners.com
            </Link>
          </Typography>
        </Box>

        <Box mt={3} textAlign='center'>
          <MButton
            variant='contained'
            size='large'
            rounded='true'
            texttransform='none'
            sx={{ width: '200px' }}
            onClick={handleLogin}
          >
            Go Back
          </MButton>
        </Box>
      </Box>
    </Dialog>
  )
}
